import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix the well-known Leaflet + webpack broken-marker-icon issue by
// importing the marker assets directly so nothing 404s in production.
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRACTICE_AREAS = [
  { value: '', label: 'All Practice Areas' },
  { value: 'asylum', label: 'Asylum' },
  { value: 'family', label: 'Family-Based' },
  { value: 'employment', label: 'Employment-Based' },
  { value: 'deportation', label: 'Deportation Defense' },
  { value: 'naturalization', label: 'Naturalization' },
  { value: 'other', label: 'Other' },
];

const PRACTICE_AREA_LABELS = PRACTICE_AREAS.reduce((acc, pa) => {
  if (pa.value) acc[pa.value] = pa.label;
  return acc;
}, {});

const LANGUAGES = [
  '', 'English', 'Spanish', 'French', 'Mandarin', 'Cantonese', 'Vietnamese',
  'Tagalog', 'Arabic', 'Portuguese', 'Russian', 'Korean', 'Haitian Creole',
  'Amharic', 'Somali', 'Hindi', 'Punjabi', 'Urdu', 'Farsi/Persian',
];

const DISCLAIMER = 'Not a lawyer referral service. Does not endorse or recommend any attorney. Verify credentials independently.';

const escapeHtml = (str = '') =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const ProviderCard = ({ provider }) => {
  const practiceAreas = provider.practice_areas || [];
  const languages = provider.languages || [];

  return (
    <Link
      to={`/find-legal-help/${provider.id}`}
      className="block bg-white rounded-xl p-5 border border-slate-200 hover:border-blue-300 transition-all duration-200 hover:shadow-lg"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-navy-700">{provider.display_name}</h3>
          <div className="flex items-center flex-wrap gap-2 mt-1">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
              <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                {provider.provider_type === 'firm' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 0h.008v.008h-.008V7.5z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
                )}
              </svg>
              {provider.provider_type === 'firm' ? 'Law Firm' : 'Attorney'}
            </span>
            {provider.is_verified && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                </svg>
                Verified
              </span>
            )}
          </div>
        </div>
      </div>

      {(provider.city || provider.state) && (
        <p className="text-sm text-slate-500 mt-2 flex items-center">
          <svg className="w-4 h-4 mr-1 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
          </svg>
          {[provider.city, provider.state].filter(Boolean).join(', ')}
        </p>
      )}

      {practiceAreas.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {practiceAreas.map((pa) => (
            <span key={pa} className="text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full">
              {PRACTICE_AREA_LABELS[pa] || pa}
            </span>
          ))}
        </div>
      )}

      {languages.length > 0 && (
        <p className="text-xs text-slate-500 mt-2 flex items-center">
          <svg className="w-3.5 h-3.5 mr-1 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
          </svg>
          {languages.join(', ')}
        </p>
      )}

      <div className="text-sm font-semibold text-blue-600 mt-3">View Profile &rarr;</div>
    </Link>
  );
};

const ProviderSearch = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    q: '', practice_area: '', language: '', state: '', city: '', zip: '',
  });
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [geo, setGeo] = useState(null); // { lat, lng } once "find near me" succeeds
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoMessage, setGeoMessage] = useState('');

  const [providers, setProviders] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const limit = 20;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  // Initialize the Leaflet map once on mount.
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current).setView([39.8283, -98.5795], 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  const buildParams = (skipVal, geoOverride) => {
    const params = { limit, skip: skipVal };
    if (filters.q) params.q = filters.q;
    if (filters.practice_area) params.practice_area = filters.practice_area;
    if (filters.language) params.language = filters.language;
    if (filters.state) params.state = filters.state;
    if (filters.city) params.city = filters.city;
    if (filters.zip) params.zip = filters.zip;
    const activeGeo = geoOverride !== undefined ? geoOverride : geo;
    if (activeGeo) {
      params.lat = activeGeo.lat;
      params.lng = activeGeo.lng;
      params.radius_miles = radiusMiles || 25;
    }
    return params;
  };

  const doSearch = async (skipVal = 0, geoOverride = undefined, append = false) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/providers/search`, { params: buildParams(skipVal, geoOverride) });
      const results = res.data.providers || [];
      setProviders((prev) => (append ? [...prev, ...results] : results));
      setTotal(res.data.total || 0);
      setSkip(skipVal);
    } catch (err) {
      console.error('Provider search failed:', err);
      setError('Failed to load providers. Please try again.');
      if (!append) {
        setProviders([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial load.
  useEffect(() => {
    doSearch(0);
  }, []);

  // Refresh markers whenever the result set changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];

    const withLocation = providers.filter(
      (p) => p.location && Array.isArray(p.location.coordinates) && p.location.coordinates.length === 2
    );

    withLocation.forEach((p) => {
      const [lng, lat] = p.location.coordinates;
      const marker = L.marker([lat, lng]).addTo(map);
      const cityState = [p.city, p.state].filter(Boolean).join(', ');
      marker.bindPopup(
        `<div style="min-width:160px">
          <strong>${escapeHtml(p.display_name)}</strong>${p.is_verified ? ' <span style="color:#10b981">&#10003;</span>' : ''}<br/>
          ${cityState ? `<span style="color:#64748b">${escapeHtml(cityState)}</span><br/>` : ''}
          <a href="/find-legal-help/${p.id}" style="color:#2563eb;font-weight:600">View profile &rarr;</a>
        </div>`
      );
      markersRef.current.push(marker);
    });

    if (withLocation.length > 0) {
      const bounds = L.latLngBounds(withLocation.map((p) => [p.location.coordinates[1], p.location.coordinates[0]]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [providers]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    doSearch(0);
  };

  const handleLoadMore = () => {
    doSearch(skip + limit, undefined, true);
  };

  const handleFindNearMe = () => {
    if (!navigator.geolocation) {
      setGeoMessage('Geolocation is not supported by your browser. Try searching by ZIP or city instead.');
      return;
    }

    setGeoLoading(true);
    setGeoMessage('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextGeo = { lat: position.coords.latitude, lng: position.coords.longitude };
        setGeo(nextGeo);
        setGeoLoading(false);
        setGeoMessage(`Showing providers within ${radiusMiles} miles of your location.`);
        doSearch(0, nextGeo);
      },
      (err) => {
        setGeoLoading(false);
        setGeo(null);
        if (err.code === err.PERMISSION_DENIED) {
          setGeoMessage('Location access denied — showing results based on your filters instead.');
        } else {
          setGeoMessage('Could not determine your location. Showing results based on your filters instead.');
        }
      },
      { timeout: 10000 }
    );
  };

  const clearGeo = () => {
    setGeo(null);
    setGeoMessage('');
    doSearch(0, null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-navy-700 to-blue-700 border-b border-blue-800">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center space-x-2.5 cursor-pointer" onClick={() => navigate('/')}>
            <div className="relative w-9 h-9 flex-shrink-0">
              <svg className="w-9 h-9" viewBox="0 0 36 36" fill="none">
                <path d="M18 2L4 8v10c0 8.5 6 16 14 18 8-2 14-9.5 14-18V8L18 2z" fill="#1e3a5f" />
                <path d="M18 5L7 10v8.5C7 25.5 12 31.5 18 33c6-1.5 11-7.5 11-14.5V10L18 5z" fill="#2563eb" />
                <circle cx="12" cy="13" r="1.2" fill="white" opacity="0.9" />
                <circle cx="18" cy="11" r="1.2" fill="white" opacity="0.9" />
                <circle cx="24" cy="13" r="1.2" fill="white" opacity="0.9" />
                <circle cx="15" cy="18" r="1.2" fill="white" opacity="0.9" />
                <circle cx="21" cy="18" r="1.2" fill="white" opacity="0.9" />
                <path d="M13 23l3.5 3.5L24 19" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <span className="text-base font-bold leading-tight block text-white">My Immigration</span>
              <span className="text-xs font-semibold leading-tight block tracking-wide uppercase text-blue-200">Services</span>
            </div>
          </div>
          <nav aria-label="Provider search">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 text-slate-200 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              Back to Home
            </button>
          </nav>
        </div>
      </header>

      <main id="main-content" className="container mx-auto px-4 py-8">
        <div className="mb-6 text-center">
          <h2 className="text-3xl font-bold text-navy-700 mb-2">Find an Immigration Attorney</h2>
          <p className="text-slate-500">Search verified immigration attorneys and law firms near you.</p>
        </div>

        {/* Filters */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="lg:col-span-2">
              <label htmlFor="filter-q" className="block text-slate-700 text-sm font-medium mb-1">Search</label>
              <input
                id="filter-q"
                type="text"
                name="q"
                value={filters.q}
                onChange={handleFilterChange}
                placeholder="Name, bio keyword..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <div>
              <label htmlFor="filter-practice-area" className="block text-slate-700 text-sm font-medium mb-1">Practice Area</label>
              <select
                id="filter-practice-area"
                name="practice_area"
                value={filters.practice_area}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              >
                {PRACTICE_AREAS.map((pa) => (
                  <option key={pa.value} value={pa.value}>{pa.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="filter-language" className="block text-slate-700 text-sm font-medium mb-1">Language</label>
              <select
                id="filter-language"
                name="language"
                value={filters.language}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="">All Languages</option>
                {LANGUAGES.filter(Boolean).map((lang) => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="filter-state" className="block text-slate-700 text-sm font-medium mb-1">State</label>
              <input
                id="filter-state"
                type="text"
                name="state"
                value={filters.state}
                onChange={handleFilterChange}
                placeholder="e.g. CA"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <div>
              <label htmlFor="filter-city" className="block text-slate-700 text-sm font-medium mb-1">City</label>
              <input
                id="filter-city"
                type="text"
                name="city"
                value={filters.city}
                onChange={handleFilterChange}
                placeholder="e.g. Los Angeles"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <div>
              <label htmlFor="filter-zip" className="block text-slate-700 text-sm font-medium mb-1">ZIP Code</label>
              <input
                id="filter-zip"
                type="text"
                name="zip"
                value={filters.zip}
                onChange={handleFilterChange}
                placeholder="e.g. 90001"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-4">
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-6 rounded-lg transition-colors disabled:opacity-50 font-semibold text-sm"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>

            <button
              type="button"
              onClick={handleFindNearMe}
              disabled={geoLoading}
              className="bg-navy-700 hover:bg-navy-800 text-white py-2.5 px-6 rounded-lg transition-colors disabled:opacity-50 font-semibold text-sm flex items-center"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
              {geoLoading ? 'Locating...' : 'Find near me'}
            </button>

            {geo && (
              <>
                <select
                  value={radiusMiles}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setRadiusMiles(val);
                    doSearch(0, geo);
                  }}
                  className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-sm"
                >
                  <option value={10}>within 10 mi</option>
                  <option value={25}>within 25 mi</option>
                  <option value={50}>within 50 mi</option>
                  <option value={100}>within 100 mi</option>
                </select>
                <button
                  type="button"
                  onClick={clearGeo}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium underline"
                >
                  Clear location
                </button>
              </>
            )}
          </div>

          {geoMessage && (
            <p className="text-sm text-blue-700 mt-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              {geoMessage}
            </p>
          )}
        </form>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 font-medium text-sm">
            {error}
          </div>
        )}

        {/* Results + Map, two-pane responsive layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Results list */}
          <div>
            <p className="text-sm text-slate-500 font-medium mb-3">
              {loading && providers.length === 0 ? 'Searching...' : `${total} provider${total === 1 ? '' : 's'} found`}
            </p>

            {!loading && providers.length === 0 && (
              <div className="bg-white rounded-xl p-8 text-center border border-slate-200">
                <svg className="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
                <p className="text-navy-700 font-medium">No providers found. Try adjusting your filters.</p>
              </div>
            )}

            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
              {providers.map((p) => (
                <ProviderCard key={p.id} provider={p} />
              ))}
            </div>

            {providers.length > 0 && providers.length < total && (
              <div className="text-center mt-4">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </div>

          {/* Map */}
          <div>
            <div
              ref={mapContainerRef}
              className="w-full h-[400px] lg:h-[600px] rounded-xl border border-slate-200"
            />
          </div>
        </div>
      </main>

      {/* Compliance disclaimer */}
      <footer className="bg-white border-t border-slate-200 py-6 mt-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-xs text-slate-400 max-w-3xl mx-auto">
            {DISCLAIMER}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ProviderSearch;
